import { useState, useEffect, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/database';
import { Wheel, WheelEntry, SpinHistory } from '../types';
import * as wheelsRepo from '../db/wheelsRepo';
import * as entriesRepo from '../db/entriesRepo';
import * as historyRepo from '../db/historyRepo';

export function useWheel() {
  const [activeWheelId, setActiveWheelId] = useState<string | null>(() => {
    return localStorage.getItem('chittiwin_active_wheel_id') || null;
  });

  // Query all wheels
  const wheels = useLiveQuery(() => db.wheels.orderBy('createdAt').toArray(), []) || [];

  // If no active wheel or active wheel is invalid, default to the first available wheel
  useEffect(() => {
    if (wheels.length > 0) {
      if (!activeWheelId || !wheels.some((w) => w.id === activeWheelId)) {
        const firstId = wheels[0].id;
        setActiveWheelId(firstId);
        localStorage.setItem('chittiwin_active_wheel_id', firstId);
      }
    }
  }, [wheels, activeWheelId]);

  const selectWheel = useCallback((id: string) => {
    setActiveWheelId(id);
    localStorage.setItem('chittiwin_active_wheel_id', id);
  }, []);

  // Active wheel data
  const currentWheel: Wheel | undefined = useLiveQuery(
    () => (activeWheelId ? db.wheels.get(activeWheelId) : undefined),
    [activeWheelId]
  );

  // Entries for active wheel
  const entries: WheelEntry[] = useLiveQuery(
    () => (activeWheelId ? db.entries.where('wheelId').equals(activeWheelId).sortBy('position') : []),
    [activeWheelId]
  ) || [];

  // Spin History for active wheel
  const history: SpinHistory[] = useLiveQuery(
    () => (activeWheelId ? db.history.where('wheelId').equals(activeWheelId).reverse().sortBy('timestamp') : []),
    [activeWheelId]
  ) || [];

  // Wheel operations
  const createNewWheel = useCallback(
    async (name: string, initialEntries?: string[], colorThemeId?: string) => {
      const newWheel = await wheelsRepo.createWheel(name, initialEntries, colorThemeId);
      selectWheel(newWheel.id);
      return newWheel;
    },
    [selectWheel]
  );

  const updateCurrentWheel = useCallback(
    async (updates: Partial<Omit<Wheel, 'id' | 'createdAt'>>) => {
      if (!activeWheelId) return;
      await wheelsRepo.updateWheel(activeWheelId, updates);
    },
    [activeWheelId]
  );

  const duplicateCurrentWheel = useCallback(async () => {
    if (!activeWheelId) return null;
    const duplicated = await wheelsRepo.duplicateWheel(activeWheelId);
    if (duplicated) {
      selectWheel(duplicated.id);
    }
    return duplicated;
  }, [activeWheelId, selectWheel]);

  const deleteCurrentWheel = useCallback(async () => {
    if (!activeWheelId) return;
    await wheelsRepo.deleteWheel(activeWheelId);
    // Switch to remaining wheel
    const remaining = await wheelsRepo.getAllWheels();
    if (remaining.length > 0) {
      selectWheel(remaining[0].id);
    } else {
      // If all deleted, recreate a default wheel
      const defaultWheel = await wheelsRepo.createWheel('🎯 My Lucky Wheel');
      selectWheel(defaultWheel.id);
    }
  }, [activeWheelId, selectWheel]);

  // Entry operations
  const addEntry = useCallback(
    async (label: string, color?: string, weight?: number) => {
      if (!activeWheelId) return;
      return await entriesRepo.addEntry(activeWheelId, label, color, weight);
    },
    [activeWheelId]
  );

  const bulkAddEntries = useCallback(
    async (labels: string[]) => {
      if (!activeWheelId) return [];
      return await entriesRepo.bulkAddEntries(activeWheelId, labels);
    },
    [activeWheelId]
  );

  const updateEntry = useCallback(
    async (entryId: string, updates: Partial<Omit<WheelEntry, 'id' | 'wheelId' | 'createdAt'>>) => {
      await entriesRepo.updateEntry(entryId, updates);
    },
    []
  );

  const deleteEntry = useCallback(async (entryId: string) => {
    await entriesRepo.deleteEntry(entryId);
  }, []);

  const clearAllEntries = useCallback(async () => {
    if (!activeWheelId) return;
    await entriesRepo.clearAllEntries(activeWheelId);
  }, [activeWheelId]);

  const shuffleEntries = useCallback(async () => {
    if (!activeWheelId) return;
    await entriesRepo.shuffleEntriesInWheel(activeWheelId);
  }, [activeWheelId]);

  const sortEntriesAZ = useCallback(async () => {
    if (!activeWheelId) return;
    await entriesRepo.sortEntriesAZInWheel(activeWheelId);
  }, [activeWheelId]);

  const deduplicateEntries = useCallback(async () => {
    if (!activeWheelId) return 0;
    return await entriesRepo.deduplicateEntriesInWheel(activeWheelId);
  }, [activeWheelId]);

  // History operations
  const logWinner = useCallback(
    async (winnerId: string, winnerLabel: string) => {
      if (!activeWheelId) return;
      return await historyRepo.logWinnerHistory(activeWheelId, winnerId, winnerLabel);
    },
    [activeWheelId]
  );

  const deleteHistory = useCallback(async (historyId: string) => {
    await historyRepo.deleteHistoryItem(historyId);
  }, []);

  const clearHistory = useCallback(async () => {
    if (!activeWheelId) return;
    await historyRepo.clearHistoryForWheel(activeWheelId);
  }, [activeWheelId]);

  return {
    wheels,
    currentWheel,
    activeWheelId,
    entries,
    history,
    selectWheel,
    createNewWheel,
    updateCurrentWheel,
    duplicateCurrentWheel,
    deleteCurrentWheel,
    addEntry,
    bulkAddEntries,
    updateEntry,
    deleteEntry,
    clearAllEntries,
    shuffleEntries,
    sortEntriesAZ,
    deduplicateEntries,
    logWinner,
    deleteHistory,
    clearHistory,
  };
}
